import React, { useState, useRef, useEffect } from 'react';
import { useContextHook } from 'use-context-hook';
import { AuthContext } from '../../../Context/authContext';
import Toast from '../../molecules/Toast';
import Button from '../../molecules/Button';
import {
    StyledCsvUpload,
    AccessDenied,
    StepsContainer,
    StepItem,
    StepCircle,
    StepLabel,
    StepConnector,
    SectionHeader,
    ProgressSection,
    ActionBar,
} from './BulkCsvUpload.styles';
import { normalize, readCSVHeaderOnly } from '../../../helpers/common';
import Form, { useForm } from '../../molecules/Form';
import DragableComponent from '../DraggableComponent/DragableComponent';
import uploadService from '../../../services/uploadService';
import Select from "../../molecules/Select"

const ALLOWED_EMAILS = [
    'ghous@webevis.com',
    'hanifghous@gmail.com',
    'jan.kozak-giegerich@atomic-computing.com',
];

// Backend-expected field keys
const predefinedFields = [
    { key: 'first_name', label: 'First Name', editable: false, required: false },
    { key: 'last_name', label: 'Last Name', editable: false, required: false },
    { key: 'person_email', label: 'Person Email', editable: false, required: true },
    { key: 'person_email_status', label: 'Person Email Status', editable: false, required: true },
    { key: 'person_title', label: 'Person Title', editable: false, required: false },
    { key: 'person_function', label: 'Person Function', editable: false, required: false },
    { key: 'person_seniority', label: 'Person Seniority', editable: false, required: false },
    { key: 'person_phone', label: 'Person Phone', editable: false, required: false },
    { key: 'person_department', label: 'Person Department', editable: false, required: false },
    { key: 'person_linkedin_url', label: 'Person LinkedIn Url', editable: false, required: false },
    { key: 'person_city', label: 'Person City', editable: false, required: false },
    { key: 'person_country', label: 'Person Country', editable: false, required: false },
    { key: 'person_state', label: 'Person State', editable: false, required: false },
    { key: 'job_start_date', label: 'Job Start Date', editable: false, required: false },
    { key: 'current_organization_id', label: 'Current Organization Id', editable: false, required: false },
    { key: 'person_linkedin_connections', label: 'Person LinkedIn Connections', editable: false, required: false },
    { key: 'organization_id', label: 'Organization Id', editable: false, required: false },
    { key: 'company_name', label: 'Company Name', editable: false, required: false },
    { key: 'company_email', label: 'Company Email', editable: false, required: false },
    { key: 'company_phone', label: 'Company Phone', editable: false, required: false },
    { key: 'number_of_employees', label: 'Number of Employees', editable: false, required: false },
    { key: 'company_website_url', label: 'Company Website Url', editable: false, required: false },
    { key: 'company_linkedin_url', label: 'Company Linkedin Url', editable: false, required: false },
    { key: 'company_facebook_url', label: 'Company Facebook Url', editable: false, required: false },
    { key: 'company_twitter_url', label: 'Company Twitter Url', editable: false, required: false },
    { key: 'company_languages', label: 'Company Languages', editable: false, required: false },
    { key: 'company_industries', label: 'Company Industries', editable: false, required: false },
    { key: 'company_revenew', label: 'Company Revenue', editable: false, required: false },
    { key: 'company_short_description', label: 'Company Short Description', editable: false, required: false },
    { key: 'company_technologies', label: 'Company Technologies', editable: false, required: false },
    { key: 'company_year_founded', label: 'Company Year Founded', editable: false, required: false },
    { key: 'company_funding', label: 'Company Funding', editable: false, required: false },
    { key: 'company_number_of_investors', label: 'Company Number of Investors', editable: false, required: false },
    { key: 'company_keywords', label: 'Company Keywords', editable: false, required: false },
    { key: 'company_location', label: 'Company Location', editable: false, required: false },
    { key: 'company_hq_city', label: 'Company HQ City', editable: false, required: false },
    { key: 'company_postal_code', label: 'Company Postal Code', editable: false, required: false },
];
const fileTypeOptions = [
    { value: 'person', label: 'Person' },
    { value: 'company', label: 'Company' },
]
const BulkCsvUpload = () => {
    const isCsvEnabled = process.env.REACT_APP_BULK_CSV_UPLOAD === 'true';
    console.log("isCsvEnabled: ", isCsvEnabled, process.env.REACT_APP_BULK_CSV_UPLOAD);
    const [form] = useForm();
    const [formData, setFormData] = useState(null);
    const [currentStep, setCurrentStep] = useState(1);
    const { user } = useContextHook(AuthContext, v => ({
        user: v.user,
    }));
    const userData = user?.data || user;
    const userEmail = userData?.email;
    const isAllowed = ALLOWED_EMAILS.includes(userEmail);
    const [dragItems, setDragItems] = useState({ root: [], container1: [] });
    const [dragableData, setDragableData] = useState(predefinedFields);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(null);
    const [statusInfo, setStatusInfo] = useState(null);
    const [uploadDone, setUploadDone] = useState(false);
    const pollRef = useRef(null);
    const fileInputRef = useRef(null);
    const [fileType, setFileType] = useState(fileTypeOptions[0])

    // Cleanup polling on unmount
    useEffect(() => {
        return () => {
            if (pollRef.current?.stopPolling) {
                pollRef.current.stopPolling();
            }
        };

    }, []);

    // Handle file selection for bulk uploads (bypasses CSVReader to avoid parsing entire file)
    const handleFileSelect = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        const validExtensions = ['.csv', '.xlsx'];
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
        if (!validExtensions.includes(fileExtension)) {
            Toast({ type: 'error', message: 'Please upload a CSV or XLSX file' });
            return;
        }

        try {
            // Read only header without parsing entire file
            const columnNames = await readCSVHeaderOnly(file);

            if (!columnNames || columnNames.length === 0) {
                Toast({ type: 'error', message: 'CSV has no columns! Please upload a valid file.' });
                return;
            }

            setFormData({
                file,
                columnNames,
            });

            form.setFieldsValue({
                csvfile: file,
            });
        } catch (error) {
            Toast({ type: 'error', message: error?.message || 'Failed to read CSV file' });
        }
    };



    const handleContinue = () => {
        if (currentStep === 1) {
            if (!formData?.columnNames || formData.columnNames.length === 0) {
                Toast({ type: 'error', message: 'Please upload a CSV file first' });
                return;
            }
            setCurrentStep(2);
        } else if (currentStep === 2) {
            handleStartProcessing();
        }
    };

    const handleBack = () => {
        if (currentStep === 2) {
            setCurrentStep(1);
        } else if (currentStep === 3) {
            setCurrentStep(2);
        }
    };

    const handleStartProcessing = async () => {
        if (!formData?.file) {
            Toast({ type: 'error', message: 'No file available for upload' });
            return;
        }

        const columnMapping = {};
        dragableData.forEach((item, index) => {
            const mappedItem = dragItems.root?.[index];
            const csvHeader =
                mappedItem?.rowKey ||
                mappedItem?.title ||
                (typeof mappedItem === 'string' ? mappedItem : null);

            if (csvHeader) {
                columnMapping[item.key] = csvHeader;
            }
        });

        if (Object.keys(columnMapping).length === 0) {
            Toast({ type: 'error', message: 'Please map at least one column' });
            return;
        }
        setCurrentStep(3);
        setUploading(true);
        setUploadDone(false);
        setProgress(null);
        setStatusInfo(null);

        try {
            const result = await uploadService.uploadLargeCSV({
                file: formData.file,
                columnMapping,
                fileType: fileType.value,
                onProgress: progressData => {
                    setProgress(progressData);
                },
                onStatusUpdate: statusData => {
                    setStatusInfo(statusData);
                },
                onComplete: finalData => {
                    setUploading(false);
                    setUploadDone(true);
                    Toast({ type: 'success', message: 'CSV upload and processing completed!' });
                },
                onError: err => {
                    setUploading(false);
                    Toast({ type: 'error', message: err?.message || 'Upload failed' });
                },
            });
            pollRef.current = result;
        } catch (err) {
            setUploading(false);
            Toast({ type: 'error', message: err?.message || 'Upload failed' });
        }
    };

    const handleFileRemove = () => {
        if (pollRef.current?.stopPolling) {
            pollRef.current.stopPolling();
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        form.setFieldsValue({ csvfile: '' });
        setFormData(null);
        setCurrentStep(1);
        setDragItems({ root: [], container1: [] });
        setProgress(null);
        setStatusInfo(null);
        setUploadDone(false);
        setUploading(false);
    };

    const handleReset = () => {
        handleFileRemove();
    };

    const handleAddField = () => {
        setDragableData(prev => {
            const updated = [...prev, { key: 'newKey', label: 'New Field', editable: true, required: false }];

            setDragItems(prevItems => ({
                ...prevItems,
                root: [...prevItems.root, null],
            }));

            return updated;
        });
    };

    const handleEditLabel = (index, newLabel) => {
        setDragableData(prev => {
            const updated = [...prev];
            updated[index].label = newLabel;
            updated[index].key = newLabel.toLowerCase().replace(/\s+/g, '_');
            return updated;
        });
    };

    if (!isCsvEnabled) {
        return (
            <AccessDenied>
                <div className="lock-icon">🔒</div>
                <div className="denied-title">Page not Allowed</div>
                <div className="denied-text">This page is not allowed for now.</div>
            </AccessDenied>
        );
    }
    if (!isAllowed) {
        return (
            <AccessDenied>
                <div className="lock-icon">🔒</div>
                <div className="denied-title">Access Denied</div>
                <div className="denied-text">You do not have permission to access this page.</div>
            </AccessDenied>
        );
    }

    return (
        <StyledCsvUpload>
            <div className="csv-upload-container">
                <h1 className="page-title">CSV Bulk Upload</h1>
                <p className="page-subtitle">Upload large CSV files with column mapping for lead imports.</p>

                <StepsContainer>
                    <StepItem>
                        <StepCircle $isActive={currentStep >= 1}>
                            {currentStep > 1 ? '✓' : '1'}
                        </StepCircle>
                        <StepLabel $isActive={currentStep >= 1} $isCurrent={currentStep === 1}>
                            CSV Upload
                        </StepLabel>
                    </StepItem>
                    <StepConnector $isActive={currentStep >= 2} />
                    <StepItem>
                        <StepCircle $isActive={currentStep >= 2}>
                            {currentStep > 2 ? '✓' : '2'}
                        </StepCircle>
                        <StepLabel $isActive={currentStep >= 2} $isCurrent={currentStep === 2}>
                            Column Mapping
                        </StepLabel>
                    </StepItem>
                    <StepConnector $isActive={currentStep >= 3} />
                    <StepItem>
                        <StepCircle $isActive={currentStep >= 3}>
                            {uploadDone ? '✓' : '3'}
                        </StepCircle>
                        <StepLabel $isActive={currentStep >= 3} $isCurrent={currentStep === 3}>
                            Processing
                        </StepLabel>
                    </StepItem>
                </StepsContainer>

                <Form className="form-area bulk-csv-upload" form={form}>
                    {currentStep === 1 && (
                        <>

                            <Select
                                label="File Type"
                                options={fileTypeOptions}
                                value={fileType}
                                onChange={(e) => setFileType(e.target.value)}
                                width={'60%'}
                                height={'40px'}
                            />
                            <div
                                style={{
                                    border: '2px dashed var(--gray-150)',
                                    borderRadius: '12px',
                                    padding: '38px',
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    backgroundColor: 'var(--gray)',
                                    transition: 'all 0.3s ease',
                                }}
                                onDragOver={(e) => {
                                    e.preventDefault();
                                    e.currentTarget.style.borderColor = 'var(--blue)';
                                    e.currentTarget.style.backgroundColor = 'rgba(36, 125, 243, 0.05)';
                                }}
                                onDragLeave={(e) => {
                                    e.preventDefault();
                                    e.currentTarget.style.borderColor = 'var(--gray-150)';
                                    e.currentTarget.style.backgroundColor = 'var(--gray)';
                                }}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    e.currentTarget.style.borderColor = 'var(--gray-150)';
                                    e.currentTarget.style.backgroundColor = 'var(--gray)';
                                    const file = e.dataTransfer.files?.[0];
                                    if (file) {
                                        const event = { target: { files: [file] } };
                                        handleFileSelect(event);
                                    }
                                }}
                                onClick={() => fileInputRef.current?.click()}>
                                {formData?.file ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                                        <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--secondary)' }}>
                                            {formData.file.name}
                                        </div>
                                        <div style={{ fontSize: '12px', color: 'var(--light-gray)' }}>
                                            {(formData.file.size / (1024 * 1024)).toFixed(2)} MB
                                        </div>
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleFileRemove();
                                            }}
                                            style={{
                                                marginTop: '8px',
                                                padding: '4px 12px',
                                                background: 'transparent',
                                                border: '1px solid var(--gray-150)',
                                                borderRadius: '6px',
                                                color: 'var(--secondary)',
                                                cursor: 'pointer',
                                                fontSize: '12px',
                                            }}>
                                            Remove
                                        </button>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                                        <div style={{ fontSize: '16px', fontWeight: 600, color: 'var(--secondary)' }}>
                                            Drag & Drop or choose file to upload
                                        </div>
                                        <div style={{ fontSize: '13px', color: 'var(--light-gray)' }}>
                                            CSV, XLSX
                                        </div>
                                    </div>
                                )}
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".csv,.xlsx"
                                    style={{ display: 'none' }}
                                    onChange={handleFileSelect}
                                />
                            </div>

                            {formData && (
                                <ActionBar>
                                    <Button
                                        newStyle
                                        rounded
                                        variant="darkBlue"
                                        width="155px"
                                        onClick={handleContinue}>
                                        Continue
                                    </Button>
                                </ActionBar>
                            )}
                        </>
                    )}

                    {currentStep === 2 && formData?.columnNames && (
                        <div>
                            <SectionHeader>
                                <h3 className="section-title">
                                    Map your CSV columns
                                </h3>
                                <p className="section-subtitle">
                                    Drag and drop CSV columns to map them with the required fields. Fields marked as required must be mapped.
                                </p>
                            </SectionHeader>
                            <DragableComponent
                                initialItems={{
                                    root: Array(dragableData.length).fill(null),
                                    container1:
                                        formData.columnNames
                                            ?.filter(name => name && name.trim() !== '')
                                            ?.map((name, index) => ({
                                                id: `bulk-col-${index}`,
                                                title: name,
                                                rowKey: name,
                                                originalIndex: index,
                                            })) || [],
                                }}
                                onItemsChange={setDragItems}
                                dragableData={dragableData}
                                onEditLabel={handleEditLabel}
                                onAddField={handleAddField}
                            />
                            <ActionBar $align="space-between">
                                <Button
                                    newStyle
                                    variant="outline"
                                    width="155px"
                                    rounded
                                    onClick={handleBack}>
                                    Back
                                </Button>
                                <Button
                                    newStyle
                                    rounded
                                    variant="darkBlue"
                                    width="155px"
                                    onClick={handleContinue}
                                >
                                    Continue
                                </Button>
                            </ActionBar>
                        </div>
                    )}

                    {currentStep === 3 && (
                        <div>
                            <SectionHeader>
                                <h3 className="section-title">
                                    Processing your CSV file
                                </h3>
                                <p className="section-subtitle">
                                    Your file is being uploaded and processed. This may take some time depending on the file size.
                                </p>
                            </SectionHeader>

                            {(progress || statusInfo) && (
                                <ProgressSection>
                                    {progress && (
                                        <>
                                            <div className="progress-title">
                                                <span>Progress</span>
                                                <span>
                                                    {progress.percent || 0}%
                                                </span>
                                            </div>
                                            <div className="progress-bar-wrapper">
                                                <div
                                                    className="progress-bar-fill"
                                                    style={{ width: `${progress.percent || 0}%` }}
                                                />
                                            </div>
                                            <p className="progress-message">
                                                {progress.message || 'Processing...'}
                                            </p>
                                        </>
                                    )}

                                    {statusInfo && (
                                        <div className="status-info">
                                            <span className={`status-badge ${statusInfo.status || 'processing'}`}>
                                                {statusInfo.status}
                                            </span>
                                            {statusInfo.totalRows > 0 && (
                                                <span>
                                                    {statusInfo.processedRows} / {statusInfo.totalRows} rows processed
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </ProgressSection>
                            )}

                            {uploadDone && (
                                <ActionBar>
                                    <Button
                                        newStyle
                                        rounded
                                        variant="darkBlue"
                                        width="200px"
                                        onClick={handleReset}>
                                        Upload Another File
                                    </Button>
                                </ActionBar>
                            )}

                            {!uploadDone && (
                                <ActionBar>
                                    <Button
                                        newStyle
                                        variant="outline"
                                        width="155px"
                                        rounded
                                        onClick={handleBack}
                                        disabled={uploading}>
                                        Back
                                    </Button>
                                </ActionBar>
                            )}
                        </div>
                    )}
                </Form>
            </div>
        </StyledCsvUpload>
    );
};

export default BulkCsvUpload;